import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  DeviationBadge,
  KaratWeightCalc,
  KsaLinkMatrix,
  FaqAccordion,
  buildKsaSchema,
  arDateParts,
  goldRatesQueryOptions,
} from "@/components/sa-ar/shell";
import { computeSaPrices, formatSar, SA_CITY_DATA } from "@/lib/sa-gold/data";
import { useGoldRates } from "@/lib/nonblocking-rates";

const SLUG = "/sa/ar/gold-karat-21";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "كم سعر جرام الذهب عيار 21 اليوم في السعودية شراء؟", a: "سعر شراء التاجر لجرام عيار 21 من العميل يقل عن سعر السوق بحوالي 5–8 ريال/جرام (هامش التاجر). القيمة الفورية اللحظية معروضة في المصفوفة أعلاه." },
  { q: "لماذا عيار 21 هو الأكثر شعبية في السعودية والخليج؟", a: "عيار 21 (نقاوة 87.5%) يوازن بين متانة القطعة وقيمتها الاستثمارية. أقل هشاشة من عيار 22، وأقرب لقيمة الذهب الخالص من عيار 18، وهو المفضل للمهور والذهب اليومي في الخليج." },
  { q: "ما الفرق بين عيار 21 و 22 و 24 في الذهب؟", a: "عيار 24 خالص 99.9% للسبائك، عيار 22 نقاوة 91.6% للمشغولات الكلاسيكية، وعيار 21 نقاوة 87.5% وهو السائد في السعودية والخليج لصلابته الأعلى وتحمّله الاستخدام اليومي." },
];

export const Route = createFileRoute("/sa/ar/gold-karat-21")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const title = `سعر الذهب عيار 21 اليوم في السعودية — شراء وبيع`;
    const description = `مؤشر سعر جرام الذهب عيار 21 اليوم في السعودية بالريال السعودي: سعر البيع، سعر الشراء، جدول الأوزان الشائعة، وأسعار المدن السعودية بشكل لحظي.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "ar-SA" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SA" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "ar-SA", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildKsaSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: Karat21Page,
});

function Karat21Page() {
  const dp = arDateParts();
  return (
    <main dir="rtl" className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">KSA · عيار 21 الخليجي</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          سعر الذهب عيار 21 اليوم في السعودية شراء وبيع — {dp.day} {dp.dayNum} {dp.month} {dp.year}
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          عيار 21 هو المؤشر الأكثر متابعة في السوق السعودي والخليجي بسبب توازنه بين النقاوة والمتانة. هذه الصفحة تعرض سعر البيع وسعر الشراء الفوري بالريال السعودي مع تفصيل لكل مدينة.
        </p>
        <div className="mt-3"><DeviationBadge /></div>
      </header>
      <LiveChart title={`مؤشر سعر عيار 21 — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <BuySellSpread />
      <CityBreakdown />
      <KaratWeightCalc />
      <FaqAccordion items={FAQS} />
      <KsaLinkMatrix current={SLUG} />
    </main>
  );
}

function BuySellSpread() {
  const { data } = useGoldRates();
  const p = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz })[21];
  const rows = [
    { w: 1, label: "1 جرام" },
    { w: 5, label: "5 جرام" },
    { w: 10, label: "10 جرام" },
    { w: 25, label: "25 جرام (نصف أونصة تقريباً)" },
    { w: 50, label: "50 جرام" },
    { w: 100, label: "100 جرام" },
  ];
  const spread = 6; // 6 SAR/g buy-sell spread
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold mb-3">مؤشر سعر عيار 21 — البيع مقابل الشراء</h2>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-right">
            <tr>
              <th className="px-3 py-2">الوزن</th>
              <th className="px-3 py-2 text-left">سعر البيع (من المحل)</th>
              <th className="px-3 py-2 text-left">سعر الشراء (من العميل)</th>
              <th className="px-3 py-2 text-left">الفارق</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const sell = p * r.w;
              const buy = (p - spread) * r.w;
              return (
                <tr key={r.w} className="border-t border-amber-500/10">
                  <th className="px-3 py-2 text-right font-medium">{r.label}</th>
                  <td className="px-3 py-2 text-left font-semibold text-amber-700 dark:text-amber-300">{formatSar(sell)}</td>
                  <td className="px-3 py-2 text-left">{formatSar(buy)}</td>
                  <td className="px-3 py-2 text-left text-red-500">−{formatSar(sell - buy)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CityBreakdown() {
  const { data } = useGoldRates();
  return (
    <section className="mx-auto max-w-6xl px-4 py-4">
      <h2 className="text-lg md:text-xl font-bold mb-3">سعر جرام الذهب عيار 21 في المدن السعودية اليوم</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SA_CITY_DATA.map((c) => {
          const price = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz, cityPremium: c.premium })[21];
          return (
            <div key={c.slug} className="rounded-2xl border border-amber-500/25 bg-card p-4">
              <div className="font-semibold">{c.ar}</div>
              <div className="mt-1 text-lg font-extrabold tabular-nums text-amber-700 dark:text-amber-300">{formatSar(price)} / جرام</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}